import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  message as Message,
  Modal,
  Popover,
  Row,
  Select,
  Space,
} from "antd";
import React, { useEffect, useState } from "react";
import DateTimePicker from "react-datetime-picker/dist/entry.nostyle";

import { getSupaClient, getWorkspace } from "../../lib/supabase";
import { SlackEditor, SlackEditorPreview } from "../TextEditor/SlackEditor";

const moment = require("moment-timezone");

const { Option } = Select;
const timezones = moment.tz.names();

const ScheduledChannelPost = (props) => {
  // props.closeModal should be a function in the parent that closes this modal
  const supabase = getSupaClient();
  const workspace = getWorkspace(supabase);
  const [isChannelPreviewVisible, setChannelPreviewVisible] = useState(false);
  const [channels, setChannels] = useState([]);
  const [message, setMessage] = useState({});
  const [selectedChannel, setSelectedChannel] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [dateTimeObj, setDateTimeObj] = useState(new Date());
  const [showBlockEditor, setShowBlockEditor] = useState(false);
  const [blockKit, setBlockKit] = useState("");

  const teamId = localStorage.getItem("team-id");
  const slackId = localStorage.getItem("slack-id");

  const checkPreview = () => {
    const timeSelected = dateTimeObj && selectedTimezone;
    const allowed =
      selectedChannel && timeSelected && (message.text || blockKit);
    return allowed !== null && allowed !== "";
  };

  const getScheduledTime = () => {
    // remove timezone
    const timeStr = moment(dateTimeObj).format("YYYY-MM-DD HH:mm A");

    // add timezone
    const scheduledTime = moment.tz(
      timeStr,
      "YYYY-MM-DD HH:mm A",
      selectedTimezone
    );
    return scheduledTime;
  };

  const pullChannels = async () => {
    const { data, error } = await supabase
      .from("channels")
      .select("slack_id, name")
      .filter("is_deleted", "eq", false)
      .filter("is_archived", "eq", false)
      .filter("team", "eq", teamId);
    if (data) setChannels(data);
    if (error) console.log("error in pullChannels", error);
  };

  useEffect(() => {
    pullChannels();
    setSelectedTimezone(moment.tz.guess());
  }, []);

  const onSelectedChannelChange = (value) => {
    const channel = channels.find((ch) => ch.slack_id === value);
    setSelectedChannel(channel);
  };

  const onMessageChange = (value) => {
    setMessage(value);
  };

  const schedulePost = async () => {
    const scheduledTimeLocal = getScheduledTime().format();
    const scheduledTimeUTC = getScheduledTime().utc().format();
    const body = {
      scheduled_time_utc: scheduledTimeUTC,
      scheduled_time_local: scheduledTimeLocal,
      timezone: selectedTimezone,
      team: teamId,
      run_function: "postToChannel",
      created_by: slackId,
      sender: slackId,
      payload: {
        channel: selectedChannel,
        sender: slackId,
        team: teamId,
      },
      workspace,
    };
    if (showBlockEditor) {
      body.payload.block_kit = JSON.parse(blockKit);
    } else {
      body.payload.message = message;
    }
    const { data, error } = await supabase.from("scheduled_tasks").insert(body);
    if (error) {
      console.log("error");
      Message.error("Error in scheduling post!");
    }
    if (data) {
      Message.success("Scheduled post!");
      setChannelPreviewVisible(false);
      props.closeModal();
      window.analytics.track("Channel post");
    }
  };

  const showPreview = async () => {
    const scheduledTime = getScheduledTime();
    if (scheduledTime < moment.now()) {
      setPreviewError("Please select a time in the future!");
    } else if (showBlockEditor) {
      try {
        JSON.parse(blockKit);
        await schedulePost();
      } catch {
        setPreviewError("Blockkit is not correct!");
      }
    } else {
      setPreviewError("");
      setChannelPreviewVisible(true);
    }
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Space size="large" direction="vertical" style={{ width: "100%" }}>
        <Row>
          Select a channel:
          <Select
            showSearch
            style={{ width: 200, marginLeft: 10 }}
            placeholder="Select a channel"
            onChange={onSelectedChannelChange}
            filterOption={(input, option) => {
              return (
                option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0
              );
            }}
          >
            {channels.map(function (channel) {
              return (
                <Option
                  key={channel.slack_id}
                  value={channel.slack_id}
                  name={channel.name}
                >
                  {channel.name}
                </Option>
              );
            })}
          </Select>
        </Row>

        <Row>
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <div>Compose your message:</div>
            <Checkbox
              onChange={(e) => {
                setShowBlockEditor(e.target.checked);
              }}
            >
              Use Slack Block Kit
              <Popover
                placement="topRight"
                content={
                  <div style={{ maxWidth: 300 }}>
                    You can use Slack&apos;s Block Kit format to customize your
                    messages more! Check{" "}
                    <a
                      target="_blank"
                      href="https://app.slack.com/block-kit-builder/"
                      rel="noreferrer"
                    >
                      here
                    </a>{" "}
                    for more info, and format the message with their Block Kit
                    builder. Please make sure you paste the exact code as Dots
                    will not check for errors in format.
                  </div>
                }
              >
                <InfoCircleOutlined style={{ fontSize: 18, marginLeft: 4 }} />
              </Popover>
            </Checkbox>
          </div>
          <div
            style={{
              width: "100%",
              marginTop: 5,
            }}
          >
            {!showBlockEditor ? (
              <SlackEditor
                onChange={onMessageChange}
                initialText="Hey! :smile:"
                value={message}
                style={{ width: "100%" }}
                disablePersonalTokens
              />
            ) : (
              <textarea
                style={{
                  width: "100%",
                  height: "250px",
                  padding: 4,
                  borderRadius: 5,
                  overflowWrap: "anywhere",
                  border: "solid 1px",
                  marginTop: 4,
                }}
                onChange={(e) => setBlockKit(e.target.value)}
                placeholder="Paste your Slack Block Kit here. Please make sure it is valid Block kit on Slack’s builder (https://app.slack.com/block-kit-builder ) . Note that Dots will not check for errors so double check yourself!"
              />
            )}
          </div>
        </Row>

        <Row gutter={[0, 32]}>
          <div>Pick a time</div>
        </Row>
      </Space>
      <Row>
        <DateTimePicker
          style={{ marginBottom: 5 }}
          onChange={setDateTimeObj}
          value={dateTimeObj}
          disableClock
        />
        <Select
          placeholder="Timezone"
          onChange={setSelectedTimezone}
          showSearch
          style={{ width: 200, marginLeft: 10 }}
          value={selectedTimezone}
        >
          {timezones.map(function (val) {
            return (
              <Option key={val} value={val}>
                {val}
              </Option>
            );
          })}
        </Select>
      </Row>
      <p>(Recurring coming soon)</p>

      <Row>
        <Col span={12} offset={4} style={{ textAlign: "center" }}>
          {previewError ? (
            <Alert message={previewError} type="error" display />
          ) : null}
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            disabled={!checkPreview()}
            onClick={showPreview}
          >
            {showBlockEditor ? "Save" : "Preview"}
          </Button>
        </Col>
      </Row>

      <Modal
        title="Preview scheduled post"
        visible={isChannelPreviewVisible}
        onCancel={() => setChannelPreviewVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setChannelPreviewVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={schedulePost}>
            Submit
          </Button>,
        ]}
      >
        You are about to send the following message in{" "}
        <b>#{selectedChannel.name}</b> on{" "}
        <b>{moment(dateTimeObj).format("YYYY-MM-DD")}</b> at{" "}
        <b>{moment(dateTimeObj).format("HH:mm")}</b> as <b>yourself</b>
        <SlackEditorPreview message={message} />
      </Modal>
    </div>
  );
};

export default ScheduledChannelPost;
